import { apiFetch } from "@/lib/api";

export interface InsideSalesClientRepBadge {
  display_name: string;
  email: string | null;
  avatar_url: string | null;
}

export interface InsideSalesClientListRow {
  fs_account_id: number;
  fs_account_name: string;
  mv_client_id: number | null;
  mv_client_name: string | null;
  mv_end_user_id: number | null;
  mv_end_user_name: string | null;
  mv_client_type: string | null;
  rep_agency: string | null;
  stocking: string | null;
  owner_rep: InsideSalesClientRepBadge | null;
  responsible_rep: InsideSalesClientRepBadge | null;
}

export interface InsideSalesClientListPage {
  rows: InsideSalesClientListRow[];
  total_count: number;
}

export const INSIDE_SALES_CUSTOMER_LIST_PAGE_SIZE = 50;

export async function fetchInsideSalesClientList(options: {
  query?: string;
  limit?: number;
  offset?: number;
}): Promise<InsideSalesClientListPage> {
  const params = new URLSearchParams();
  const query = options.query?.trim() ?? "";
  if (query !== "") {
    params.set("q", query);
  }
  params.set("limit", String(options.limit ?? INSIDE_SALES_CUSTOMER_LIST_PAGE_SIZE));
  params.set("offset", String(options.offset ?? 0));
  const data = await apiFetch<InsideSalesClientListPage>(`/api/inside-sales/customers?${params.toString()}`);
  return {
    rows: data.rows,
    total_count: Number.isFinite(data.total_count) ? data.total_count : data.rows.length,
  };
}

export interface FreshsalesAccountDisplayField {
  field_key: string;
  label: string;
  value: string;
}

export interface InsideSalesClientDetailResult {
  row: Record<string, unknown>;
  freshsales_fields: FreshsalesAccountDisplayField[];
  freshdesk_company: { freshdeskCompanyId: number; freshdeskCompanyName: string } | null;
}

export interface InsideSalesCustomerRelated {
  orders: { id: number; number: string | null; status: string | null }[];
  invoices: { mv_id: number; number: string | null; status: string | null; date_created: string | null }[];
  devices: { id: number; device_label_serial: string }[];
  complaints: { id: number; title: string; status: string | null; created_at: string | null }[];
  rmas: { id: number; rma_number: string | null; status: string | null; complaint: string | null; requested_at: string | null }[];
}

export async function saveCustomerFreshdeskCompany(
  fsAccountId: number,
  freshdeskCompanyId: number | null,
): Promise<{ freshdeskCompanyId: number; freshdeskCompanyName: string } | null> {
  const data = await apiFetch<{ freshdesk_company: { freshdeskCompanyId: number; freshdeskCompanyName: string } | null }>(
    `/api/inside-sales/customers/${String(fsAccountId)}/freshdesk-company`,
    { method: "PUT", body: { freshdeskCompanyId } },
  );
  return data.freshdesk_company;
}

export interface CreateCustomerComplaintInput {
  summary: string;
  contactFsContactId: number | null;
  newContactName: string | null;
  newContactEmail: string | null;
  newContactPhone: string | null;
  freshdeskTicketIds: number[];
}

export async function createInsideSalesCustomerComplaint(
  fsAccountId: number,
  input: CreateCustomerComplaintInput,
): Promise<void> {
  await apiFetch(`/api/inside-sales/customers/${String(fsAccountId)}/complaints`, {
    method: "POST",
    body: input,
  });
}

export interface CustomerContactOption {
  fsContactId: number;
  displayName: string;
  email: string | null;
  phone: string | null;
}

export interface CustomerComplaintEventOption {
  id: number;
  summary: string | null;
  createdAt: string;
}

export interface CustomerSkuOption {
  sku: string;
  description: string | null;
}

export interface CreateCustomerRmaInput {
  companyFsAccountId: number;
  contactFsContactId: number | null;
  newContactName: string | null;
  newContactEmail: string | null;
  newContactPhone: string | null;
  reasonKind: "complaint" | "other";
  qualityEventId: number | null;
  newComplaintSummary: string | null;
  otherReason: string | null;
  lines: { sku: string; quantity: number }[];
  freshdeskTicketIds: number[];
}

export async function fetchCustomerContacts(fsAccountId: number, query: string): Promise<CustomerContactOption[]> {
  const params = new URLSearchParams();
  if (query.trim() !== "") params.set("q", query.trim());
  const data = await apiFetch<{ rows: CustomerContactOption[] }>(
    `/api/inside-sales/customers/${String(fsAccountId)}/contacts?${params.toString()}`,
  );
  return data.rows;
}

export async function fetchCustomerComplaintEvents(
  fsAccountId: number,
  query: string,
): Promise<CustomerComplaintEventOption[]> {
  const params = new URLSearchParams();
  if (query.trim() !== "") params.set("q", query.trim());
  const data = await apiFetch<{ rows: CustomerComplaintEventOption[] }>(
    `/api/inside-sales/customers/${String(fsAccountId)}/complaint-events?${params.toString()}`,
  );
  return data.rows;
}

export async function fetchCustomerRmaSkus(query: string): Promise<CustomerSkuOption[]> {
  const params = new URLSearchParams();
  if (query.trim() !== "") params.set("q", query.trim());
  const data = await apiFetch<{ rows: CustomerSkuOption[] }>(`/api/inside-sales/skus?${params.toString()}`);
  return data.rows;
}

export async function createInsideSalesCustomerRma(
  fsAccountId: number,
  input: CreateCustomerRmaInput,
): Promise<{ id: number; rmaNumber: string }> {
  return apiFetch<{ id: number; rmaNumber: string }>(`/api/inside-sales/customers/${String(fsAccountId)}/rmas`, {
    method: "POST",
    body: input,
  });
}

export interface InsideSalesCustomerTickets {
  linked: boolean;
  tickets: { id: number; subject: string | null; status: number | null; created_at: string | null; url: string }[];
}

export async function fetchInsideSalesCustomerTickets(fsAccountId: number): Promise<InsideSalesCustomerTickets> {
  return apiFetch<InsideSalesCustomerTickets>(`/api/inside-sales/customers/${String(fsAccountId)}/tickets`, {
    priority: "interactive",
  });
}

export async function fetchInsideSalesCustomerRelated(fsAccountId: number): Promise<InsideSalesCustomerRelated> {
  return apiFetch<InsideSalesCustomerRelated>(`/api/inside-sales/customers/${String(fsAccountId)}/related`, {
    priority: "interactive",
  });
}

export async function fetchInsideSalesClientDetail(fsAccountId: number): Promise<InsideSalesClientDetailResult> {
  const data = await apiFetch<{
    row: Record<string, unknown>;
    freshsales_fields?: FreshsalesAccountDisplayField[];
    freshdesk_company?: { freshdeskCompanyId: number; freshdeskCompanyName: string } | null;
  }>(`/api/inside-sales/customers/${String(fsAccountId)}`, {
    priority: "interactive",
  });
  return {
    row: data.row,
    freshsales_fields: data.freshsales_fields ?? [],
    freshdesk_company: data.freshdesk_company ?? null,
  };
}
